<script setup lang="ts">
import { computed } from 'vue'
import { RrTag, CommonStatusMap, type SemanticTone, type BadgeStatus, type TagColor } from '@xjlv2/ui'

const props = withDefaults(
  defineProps<{
    status?: BadgeStatus | string
    color?: TagColor
    tone?: SemanticTone
    text?: string | number
    label?: string | number
    dot?: boolean
    closable?: boolean
    checkable?: boolean
    checked?: boolean
  }>(),
  {
    status: undefined,
    color: undefined,
    tone: undefined,
    text: undefined,
    label: undefined,
    dot: true,
    closable: false,
    checkable: false,
    checked: false
  }
)

const emit = defineEmits<{
  close: [event: MouseEvent]
  'update:checked': [checked: boolean]
  click: [event: MouseEvent]
}>()

const ANTD_STATUSES = new Set([
  'success',
  'processing',
  'default',
  'error',
  'warning',
  'info',
  'danger',
  'neutral',
  'muted',
  'primary'
])

const normalizedStatus = computed(() => {
  if (typeof props.status !== 'string') return ''
  return props.status.trim()
})

// Check if `status` prop was provided as a recognized status code
const isStatusCode = computed(() => {
  if (!normalizedStatus.value) return false
  return ANTD_STATUSES.has(normalizedStatus.value.toLowerCase())
})

const commonMeta = computed(() => {
  if (!normalizedStatus.value) return undefined
  return CommonStatusMap[normalizedStatus.value.toUpperCase()]
})

// Resolve the tag color/tone
const effectiveColor = computed(() => {
  if (props.color) return props.color
  if (props.tone) return (props.tone as string) === 'muted' ? 'default' : props.tone
  if (isStatusCode.value) {
    const s = normalizedStatus.value.toLowerCase()
    return s === 'muted' ? 'default' : s
  }
  if (commonMeta.value) {
    return commonMeta.value.status
  }
  return 'default'
})

// Resolve the display text
const effectiveText = computed(() => {
  if (props.text !== undefined && props.text !== null && props.text !== '') return String(props.text)
  if (props.label !== undefined && props.label !== null && props.label !== '') return String(props.label)
  if (commonMeta.value) {
    return commonMeta.value.text
  }
  // If status was passed as a business text (e.g. :status="orderStatusText(...)")
  if (props.status !== undefined && props.status !== null && !isStatusCode.value) {
    return String(props.status)
  }
  return ''
})
</script>

<template>
  <RrTag
    :color="effectiveColor"
    :dot="dot"
    :closable="closable"
    :checkable="checkable"
    :checked="checked"
    @close="emit('close', $event)"
    @update:checked="emit('update:checked', $event)"
    @click="emit('click', $event)"
  >
    <template v-if="$slots.icon" #icon>
      <slot name="icon" />
    </template>
    <slot>{{ effectiveText }}</slot>
  </RrTag>
</template>
